import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCart as fetchCartAPI,
  addToCart as addToCartAPI,
  updateCartQuantity as updateCartQuantityAPI,
  removeFromCart as removeFromCartAPI,
  clearCart as clearCartAPI,
} from "@/services/cartService";
import { toast } from "react-toastify";

// ✅ Load from localStorage
const loadCart = () => {
  const storedCart = localStorage.getItem("cartItems");
  return storedCart ? JSON.parse(storedCart) : [];
};

// ✅ Background cart sync
const syncCart = async (dispatch) => {
  try {
    const updated = await fetchCartAPI();
    localStorage.setItem("cartItems", JSON.stringify(updated));
    dispatch(cartSlice.actions.setCart(updated));
  } catch (err) {
    console.warn("⚠️ Cart sync failed:", err.message);
  }
};

// ✅ Restore fetchCart for compatibility (used in authSlice)
export const fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async (_, { rejectWithValue }) => {
    try {
      const items = await fetchCartAPI();
      localStorage.setItem("cartItems", JSON.stringify(items));
      return items;
    } catch (err) {
      return rejectWithValue(err.message || "Failed to fetch cart");
    }
  }
);

// -------------------------
// 🛒 ADD ITEM (Optimistic)
// -------------------------
export const addToCartAsync = createAsyncThunk(
  "cart/addToCartAsync",
  async ({ productId, quantity = 1, variant = {} }, { dispatch, getState, rejectWithValue }) => {
    const currentCart = [...getState().cart.cartItems];
    const existing = currentCart.find(
      (i) =>
        i.id === productId &&
        JSON.stringify(i.variant || {}) === JSON.stringify(variant || {})
    );

    if (existing) existing.quantity += quantity;
    else currentCart.push({ id: productId, quantity, variant });

    dispatch(cartSlice.actions.setCart(currentCart));
    localStorage.setItem("cartItems", JSON.stringify(currentCart));
    toast.success("🛒 Item added to cart!");

    try {
      await addToCartAPI(productId, quantity, variant);
      syncCart(dispatch);
    } catch (err) {
      toast.error("❌ Failed to sync with server");
      return rejectWithValue(err.message || "Failed to add item");
    }
  }
);

// -------------------------
// 🔢 UPDATE QUANTITY (Instant)
// -------------------------
export const updateCartQuantityAsync = createAsyncThunk(
  "cart/updateCartQuantityAsync",
  async ({ productId, quantity, variant }, { dispatch, getState, rejectWithValue }) => {
    const updatedCart = getState().cart.cartItems.map((i) =>
      i.id === productId &&
      JSON.stringify(i.variant || {}) === JSON.stringify(variant || {})
        ? { ...i, quantity }
        : i
    );

    dispatch(cartSlice.actions.setCart(updatedCart));
    localStorage.setItem("cartItems", JSON.stringify(updatedCart));
    toast.info("🔄 Quantity updated");

    try {
      await updateCartQuantityAPI(productId, quantity, variant);
      syncCart(dispatch);
    } catch (err) {
      toast.error("❌ Failed to sync quantity");
      return rejectWithValue(err.message || "Failed to update quantity");
    }
  }
);

// -------------------------
// 🗑 REMOVE ITEM (Instant)
// -------------------------
export const removeFromCartAsync = createAsyncThunk(
  "cart/removeFromCartAsync",
  async ({ productId, variant }, { dispatch, getState, rejectWithValue }) => {
    const filteredCart = getState().cart.cartItems.filter(
      (i) =>
        !(
          i.id === productId &&
          JSON.stringify(i.variant || {}) === JSON.stringify(variant || {})
        )
    );

    dispatch(cartSlice.actions.setCart(filteredCart));
    localStorage.setItem("cartItems", JSON.stringify(filteredCart));
    toast.warn("🗑 Item removed from cart");

    try {
      await removeFromCartAPI(productId, variant);
      syncCart(dispatch);
    } catch (err) {
      toast.error("❌ Failed to sync removal");
      return rejectWithValue(err.message || "Failed to remove item");
    }
  }
);

// -------------------------
// 🧹 CLEAR CART (Instant)
// -------------------------
export const clearCartAsync = createAsyncThunk(
  "cart/clearCartAsync",
  async (_, { dispatch, rejectWithValue }) => {
    dispatch(cartSlice.actions.setCart([]));
    localStorage.removeItem("cartItems");
    toast.info("🧾 Cart cleared");

    try {
      await clearCartAPI();
    } catch (err) {
      toast.error("❌ Failed to sync clear");
      return rejectWithValue(err.message || "Failed to clear cart");
    }
  }
);

// -------------------------
// SLICE
// -------------------------
const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItems: loadCart(),
    status: "idle",
    error: null,
  },
  reducers: {
    setCart: (state, action) => {
      state.cartItems = action.payload;
    },
    clearCartLocal: (state) => {
      state.cartItems = [];
      localStorage.removeItem("cartItems");
    },
  },
  extraReducers: (builder) => {
    builder
      // Support for fetchCart (authSlice)
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.cartItems = action.payload;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Handle rejections from other thunks
      .addCase(addToCartAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCartQuantityAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(removeFromCartAsync.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(clearCartAsync.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { setCart, clearCartLocal } = cartSlice.actions;
export default cartSlice.reducer;
